/**
 * Shared Parameter Parser Service
 *
 * Parses Revit shared parameter files (.txt format).
 * Handles both UTF-8 and UTF-16 encoded files.
 *
 * File format:
 * - Comments start with #
 * - META section: defines version
 * - GROUP section: defines parameter groups
 * - PARAM section: defines parameters
 */

import type {
  SharedParameter,
  SharedParameterFile,
  SharedParameterDataType,
  ParameterOption,
  GroupedParameterOptions,
} from '../types/sharedParameter.types';

/**
 * Normalizes text that may have UTF-16 artifacts (spaces between chars)
 */
function normalizeText(text: string): string {
  // Check if text has UTF-16 artifact pattern (space between each char)
  if (text.includes(' ') && text.length > 2) {
    const noSpaces = text.replace(/ /g, '');
    // If removing spaces makes it look like normal text, it was UTF-16 artifact
    if (noSpaces.length < text.length / 2 + 10) {
      return noSpaces;
    }
  }
  return text;
}

/**
 * Parses a shared parameter file content into structured data
 */
export function parseSharedParameterFile(content: string): SharedParameterFile {
  const result: SharedParameterFile = {
    version: 2,
    minVersion: 1,
    groups: [],
    parameters: [],
  };

  // Normalize the content (handle UTF-16 encoding artifacts)
  const normalizedContent = normalizeText(content);

  // Split into lines
  const lines = normalizedContent.split(/\r?\n/);

  for (const rawLine of lines) {
    const line = rawLine.trim();

    // Skip empty lines and comments
    if (!line || line.startsWith('#') || line.startsWith('*')) {
      continue;
    }

    // Split by tab
    const parts = line.split('\t');

    if (parts.length < 2) continue;

    const type = parts[0].toUpperCase();

    switch (type) {
      case 'META':
        if (parts.length >= 3) {
          result.version = parseInt(parts[1], 10) || 2;
          result.minVersion = parseInt(parts[2], 10) || 1;
        }
        break;

      case 'GROUP':
        if (parts.length >= 3) {
          result.groups.push({
            id: parseInt(parts[1], 10),
            name: parts[2],
          });
        }
        break;

      case 'PARAM':
        if (parts.length >= 7) {
          const groupId = parseInt(parts[5], 10);
          const group = result.groups.find((g) => g.id === groupId);

          result.parameters.push({
            guid: parts[1],
            name: parts[2],
            dataType: parts[3] as SharedParameterDataType,
            dataCategory: parts[4] || '',
            groupId,
            groupName: group?.name,
            visible: parts[6] === '1',
            description: parts[7] || '',
            userModifiable: parts[8] === '1',
            hideWhenNoValue: parts[9] === '1',
          });
        }
        break;
    }
  }

  return result;
}

/**
 * Shared-file entries are definitions from the shared parameter file; keep
 * that origin visible in dropdown captions next to the file description.
 */
function withSharedParameterOrigin(description: string): string {
  return description ? `${description} · Shared parameter` : 'Shared parameter';
}

/**
 * Converts parsed parameters to dropdown options
 */
export function parametersToOptions(parameters: SharedParameter[]): ParameterOption[] {
  return parameters
    .filter((p) => p.visible)
    .map((p) => ({
      value: p.name,
      label: p.name,
      group: p.groupName,
      dataType: p.dataType,
      description: withSharedParameterOrigin(p.description),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Groups parameters by their group name for categorized dropdowns
 */
export function groupParameterOptions(parameters: SharedParameter[]): GroupedParameterOptions[] {
  const grouped = new Map<number, GroupedParameterOptions>();

  for (const param of parameters) {
    if (!param.visible) continue;

    if (!grouped.has(param.groupId)) {
      grouped.set(param.groupId, {
        groupName: param.groupName || `Group ${param.groupId}`,
        groupId: param.groupId,
        options: [],
      });
    }

    grouped.get(param.groupId)!.options.push({
      value: param.name,
      label: param.name,
      dataType: param.dataType,
      description: withSharedParameterOrigin(param.description),
    });
  }

  // Convert to array and sort
  const groups = Array.from(grouped.values());

  // Sort options within each group
  groups.forEach((group) => {
    group.options.sort((a, b) => a.label.localeCompare(b.label));
  });

  // Sort groups by name
  return groups.sort((a, b) => a.groupName.localeCompare(b.groupName));
}

/**
 * Filters parameters by data type
 */
export function filterParametersByType(
  parameters: SharedParameter[],
  dataTypes: SharedParameterDataType[]
): SharedParameter[] {
  return parameters.filter((p) => dataTypes.includes(p.dataType));
}

/**
 * Filters parameters by group
 */
export function filterParametersByGroup(
  parameters: SharedParameter[],
  groupIds: number[]
): SharedParameter[] {
  return parameters.filter((p) => groupIds.includes(p.groupId));
}

/**
 * Gets parameters suitable for text fields (TEXT, MULTILINETEXT)
 */
export function getTextParameters(parameters: SharedParameter[]): SharedParameter[] {
  return filterParametersByType(parameters, ['TEXT', 'MULTILINETEXT']);
}

/**
 * Gets parameters suitable for numeric fields
 */
export function getNumericParameters(parameters: SharedParameter[]): SharedParameter[] {
  return filterParametersByType(parameters, ['INTEGER', 'NUMBER', 'LENGTH', 'AREA', 'VOLUME', 'ANGLE']);
}

/**
 * Searches parameters by name (case-insensitive)
 */
export function searchParameters(
  parameters: SharedParameter[],
  query: string
): SharedParameter[] {
  const lowerQuery = query.toLowerCase();
  return parameters.filter(
    (p) =>
      p.name.toLowerCase().includes(lowerQuery) ||
      p.description.toLowerCase().includes(lowerQuery)
  );
}

/**
 * Gets parameter by name
 */
export function getParameterByName(
  parameters: SharedParameter[],
  name: string
): SharedParameter | undefined {
  return parameters.find((p) => p.name === name);
}

/**
 * Validates a shared parameter file content
 */
export function validateSharedParameterFile(content: string): {
  isValid: boolean;
  error?: string;
} {
  try {
    const parsed = parseSharedParameterFile(content);

    if (parsed.parameters.length === 0) {
      return {
        isValid: false,
        error: 'No parameters found in the file',
      };
    }

    return { isValid: true };
  } catch (error) {
    return {
      isValid: false,
      error: error instanceof Error ? error.message : 'Failed to parse file',
    };
  }
}
